import React from 'react';
import { ErrorBoundary } from './ErrorBoundary';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';

interface Props {
  children: React.ReactNode;
  workflowId?: string;
  onReset?: () => void;
}

export function WorkflowErrorBoundary({ children, workflowId, onReset }: Props) {
  const handleError = (error: Error, errorInfo: React.ErrorInfo) => {
    // Log workflow-specific context
    console.error('Workflow Error:', {
      workflowId,
      error: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack,
    });

    // TODO: Send to error tracking
    // trackWorkflowError(workflowId, error, errorInfo);
  };

  const handleReset = () => {
    // Clear any workflow-specific state
    if (workflowId) {
      // Clear workflow from cache
      sessionStorage.removeItem(`workflow-${workflowId}`);
    }
    
    onReset?.();
  };

  return (
    <ErrorBoundary
      level="page"
      onError={handleError}
      resetKeys={[workflowId]}
      fallback={
        <div className="flex items-center justify-center h-full p-8">
          <Alert variant="destructive" className="max-w-lg">
            <AlertTriangle className="h-5 w-5" />
            <AlertTitle>Workflow Error</AlertTitle>
            <AlertDescription className="mt-2">
              <p className="mb-4">
                The workflow editor encountered an error and couldn't load.
              </p>
              <p className="text-sm text-muted-foreground mb-4">
                This might be due to corrupted workflow data or a browser issue.
              </p>
              <div className="flex gap-2">
                <Button size="sm" onClick={handleReset}>
                  <RefreshCw className="mr-2 h-3 w-3" />
                  Reset Workflow
                </Button>
                <Button 
                  size="sm" 
                  variant="outline" 
                  onClick={() => window.location.href = '/workflows'}
                >
                  Back to Workflows
                </Button>
              </div>
            </AlertDescription>
          </Alert>
        </div>
      }
    >
      {children}
    </ErrorBoundary>
  );
}
